import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { playSound } from "@/lib/animations";
import { safeApiRequest } from "@/lib/api-helpers";
import { SessionService } from "@/lib/sessionService";
import { useUser } from '@/lib/hooks/use-user';
import { useFetchWithAuth } from '@/lib/fetchWithAuth';
import { X, Plus, Camera } from "lucide-react";

const COMMUNICATION_OPTIONS = [
  "Uses single words",
  "Uses short phrases",
  "Uses full sentences",
  "Uses AAC/device",
  "Uses gestures or pointing",
  "Uses sign language",
  "Uses PECS (Picture Exchange Communication System)",
  "Uses writing/typing",
  "Needs visual supports to understand language",
  "Needs extra processing time",
];

const LEARNING_PROFILE_OPTIONS = [
  "Emerging learner (needs step-by-step teaching)",
  "Learns best with visuals",
  "Needs routine and predictability",
  "Can follow 2-3 step instructions",
  "Can generalize skills across settings",
  "Needs adult support to start tasks",
  "Becoming more independent",
];

const STRENGTH_SUGGESTIONS = [
  "Good memory", "Loves letters or numbers", "Strong visual learner",
  "Very affectionate", "Motivated by praise", "Good with routines",
];

const WHAT_WORKS_SUGGESTIONS = [
  "First/Then language", "Visual schedules", "Timer warnings",
  "Songs or routines", "Choices instead of commands", "Quiet instructions",
];

const INTEREST_SUGGESTIONS = [
  "Alphabet", "Music", "Trucks", "Animals", "YouTube videos",
  "Favorite characters", "Movement activities", "Sensory toys",
];

const TRIGGER_SUGGESTIONS = [
  "Loud noises", "Sudden transitions", "Crowded places",
  "Waiting", "Certain textures", "Being rushed",
];

const SENSORY_OPTIONS = [
  "Needs movement breaks",
  "Seeks deep pressure",
  "Sensitive to sound",
  "Sensitive to touch",
  "Needs calm environment",
  "Benefits from fidgets",
  "Overwhelmed by visual clutter",
];

const SOCIAL_COMFORT_OPTIONS = [
  "Prefers adults",
  "Prefers familiar peers",
  "Struggles in groups",
  "Social but needs support",
  "Highly social",
];

const REGULATION_OPTIONS = [
  "Breaks help reset",
  "Needs warnings before transitions",
  "Calms with music",
  "Needs quiet space",
  "Responds to visuals",
];

interface ProfilePreferences {
  communicationStyle: string[];
  learningProfile: string[];
  interests: string[];
  triggers: string[];
  sensoryNeeds: string[];
  socialComfort: string;
  regulationSupports: string[];
  strengths: string;
  whatWorks: string;
}

const DEFAULT_PREFS: ProfilePreferences = {
  communicationStyle: [],
  learningProfile: [],
  interests: [],
  triggers: [],
  sensoryNeeds: [],
  socialComfort: "",
  regulationSupports: [],
  strengths: "",
  whatWorks: "",
};

function MultiSelect({ options, selected, onChange }: {
  options: string[];
  selected: string[];
  onChange: (val: string[]) => void;
}) {
  const toggle = (opt: string) => {
    onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => toggle(opt)}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            selected.includes(opt)
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-foreground border-border hover:border-primary/50"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function TagInput({ tags, onChange, suggestions, placeholder }: {
  tags: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  placeholder?: string;
}) {
  const [input, setInput] = useState("");

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput("");
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter(t => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    }
  };

  const unusedSuggestions = suggestions.filter(s => !tags.includes(s));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[36px]">
        {tags.map(tag => (
          <Badge key={tag} variant="secondary" className="gap-1 pl-2.5 pr-1 py-1">
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className="rounded-full p-0.5 hover:bg-muted">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Type and press Enter"}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={() => addTag(input)} disabled={!input.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {unusedSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-muted-foreground self-center mr-1">Suggestions:</span>
          {unusedSuggestions.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              className="text-xs px-2 py-1 rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestedTags({ suggestions, value, onAdd }: {
  suggestions: string[];
  value: string;
  onAdd: (tag: string) => void;
}) {
  const used = value.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
  const unused = suggestions.filter(s => !used.includes(s.toLowerCase()));
  if (!unused.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      <span className="text-xs text-muted-foreground self-center mr-1">Suggestions:</span>
      {unused.map(s => (
        <button
          key={s}
          type="button"
          onClick={() => onAdd(s)}
          className="text-xs px-2 py-1 rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary hover:text-primary transition-colors"
        >
          + {s}
        </button>
      ))}
    </div>
  );
}

interface HubSetupProps {
  open: boolean;
  onClose: () => void;
  onHubCreated?: (hubId: string | number) => void;
  editMode?: boolean;
  hubId?: number;
  resourceType?: string;
}

export default function HubSetup({ open, onClose, onHubCreated, editMode = false, hubId, resourceType }: HubSetupProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fetchWithAuth = useFetchWithAuth();

  const [firstName, setFirstName] = useState("");
  const [careRecipientName, setCareRecipientName] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isHubOwner, setIsHubOwner] = useState(false);
  const [recipientInactive, setRecipientInactive] = useState(false);
  const [prefs, setPrefs] = useState<ProfilePreferences>(DEFAULT_PREFS);
  const [enableCareRecipientView, setEnableCareRecipientView] = useState(false);
  const [careRecipientPin, setCareRecipientPin] = useState("");
  const [showCareRecipientSettings, setShowCareRecipientSettings] = useState(editMode);

  const [liteHasHub, setLiteHasHub] = useState(false);

  const { user: dbUser, isLoading: isLoadingUser, error: userError } = useUser();

  const updatePrefs = useCallback((update: Partial<ProfilePreferences>) => {
    setPrefs(p => ({ ...p, ...update }));
  }, []);

  const { data: hubData } = useQuery({
    queryKey: ['/api/hubs', hubId, dbUser?.id],
    queryFn: async () => {
      if (!dbUser?.id || !hubId) return Promise.reject('Not authenticated');
      const hubResponse = await safeApiRequest(`/api/managed-profiles/${hubId}`);
      const session = await SessionService.getCurrentSession();
      const extendedFields = session?.sessionData?.[`hub_${hubId}_profile`] || {};
      return { ...hubResponse, ...extendedFields };
    },
    enabled: !!hubId && editMode && !!dbUser?.id,
    staleTime: 0,
    refetchOnMount: true
  });

  useEffect(() => {
    if (dbUser?.role === 'Lite') {
      SessionService.getCurrentSession().then(session => {
        const existingHubs = session?.sessionData?.fallbackHubs?.filter((h: any) => h.ownerId === dbUser.id) || [];
        if (!editMode && existingHubs.length > 0) {
          setLiteHasHub(true);
        } else {
          setLiteHasHub(false);
        }
      }).catch(err => console.warn("Could not check Lite hubs", err));
    }
  }, [dbUser, editMode]);

  useEffect(() => {
    if (editMode && hubData) {
      const nameWithoutSuffix = hubData.name?.endsWith("'s Hub") ? hubData.name.slice(0, -7) : hubData.name || "";
      setFirstName(hubData.name || nameWithoutSuffix);
      setCareRecipientName(hubData.display_name || "");
      setAdditionalNotes(hubData.notes || "");
      setProfileId(hubData.profileId || null);
      setIsHubOwner(hubData.isOwner === true);
      setRecipientInactive(hubData.recipientInactive === true);
      setAvatarUrl(hubData.profileId && !hubData.recipientInactive ? hubData.avatarUrl || null : null);
      setEnableCareRecipientView(hubData.enableCareRecipientView === true);
      if (hubData.careRecipientPin) setCareRecipientPin(hubData.careRecipientPin);

      if (hubData.preferences && typeof hubData.preferences === 'object') {
        setPrefs({
          communicationStyle: hubData.preferences.communicationStyle || [],
          learningProfile: hubData.preferences.learningProfile || [],
          interests: hubData.preferences.interests || [],
          triggers: hubData.preferences.triggers || [],
          sensoryNeeds: hubData.preferences.sensoryNeeds || [],
          socialComfort: hubData.preferences.socialComfort || "",
          regulationSupports: hubData.preferences.regulationSupports || [],
          strengths: hubData.preferences.strengths || "",
          whatWorks: hubData.preferences.whatWorks || "",
        });
      }
    } else if (!editMode) {
      setProfileId(null);
      setIsHubOwner(true);
      setRecipientInactive(false);
      setEnableCareRecipientView(false);
      setCareRecipientPin("");
    }
  }, [editMode, hubData]);

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setAvatarUrl(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFirstName("");
    setCareRecipientName("");
    setAdditionalNotes("");
    setAvatarUrl(null);
    setProfileId(null);
    setIsHubOwner(false);
    setRecipientInactive(false);
    setPrefs(DEFAULT_PREFS);
    setEnableCareRecipientView(false);
    setCareRecipientPin("");
    setShowCareRecipientSettings(false);
  };

  const appendToTextarea = (setter: (fn: (prev: string) => string) => void, tag: string) => {
    setter((prev: string) => {
      const trimmed = prev.trim();
      if (!trimmed) return tag;
      return trimmed + ", " + tag;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dbUser?.id) return toast({ title: "Error", description: "Please log in to create a hub", variant: "destructive" });
    if (!firstName.trim()) return toast({ title: "Error", description: "Please enter a name for the hub", variant: "destructive" });
    if (enableCareRecipientView && careRecipientPin.length !== 4) return toast({ title: "Invalid PIN", description: "Please enter a 4-digit PIN.", variant: "destructive" });
    if (liteHasHub && !editMode) return toast({ title: "Upgrade Required", description: "Lite users can only have one hub. Upgrade to Plus to create multiple hubs.", variant: "destructive" });

    const formattedName = editMode ? firstName.trim() : `${firstName.trim()}'s Hub`;
    if (editMode && isHubOwner && profileId && !recipientInactive && !careRecipientName.trim()) {
      return toast({ title: "Care Recipient name required", description: "Enter the current Care Recipient's name.", variant: "destructive" });
    }

    const prefsPayload = {
      ...prefs,
    };

    let createdHub: any;
    try {
      if (editMode && hubId) {
        const res = await fetchWithAuth(`/api/managed-profiles/${hubId}`, {
          method: "PUT",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formattedName,
            description: additionalNotes.trim() || null,
            preferences: prefsPayload,
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        createdHub = await res.json();
          if (isHubOwner && profileId && !recipientInactive) {
            const recipientRes = await fetchWithAuth(`/api/hubs/${hubId}/care-recipient/save`, {
              method: "POST",
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                careRecipientId: profileId,
                displayName: careRecipientName.trim(),
                photoUrl: avatarUrl,
              }),
            });
            if (!recipientRes.ok) throw new Error(await recipientRes.text());
          }
      } else {
        const res = await fetchWithAuth("/api/hubs", {
          method: "POST",
          headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: formattedName,
              description: additionalNotes.trim() || "",
              careRecipient: {
                displayName: firstName.trim(),
                photoUrl: avatarUrl,
              },
            }),
        });
        if (!res.ok) throw new Error(await res.text());
        createdHub = await res.json();

        const profileRes = await fetchWithAuth(`/api/managed-profiles/${createdHub.id}`, {
          method: "PUT",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formattedName,
            description: additionalNotes.trim() || null,
            preferences: prefsPayload,
          }),
        });
        if (!profileRes.ok) {
          console.warn("Failed to save preferences after hub creation:", await profileRes.text());
        }
      }
    } catch (err) {
      console.error("Hub save failed:", err);
      return toast({ title: "Error", description: "Failed to save hub. Please try again.", variant: "destructive" });
    }

    try { playSound('success'); } catch {}
    toast({ title: editMode ? "Hub Updated" : "Hub Created", description: editMode ? `${formattedName} updated.` : `${formattedName} created.` });

    resetForm();
    onClose();
    await queryClient.invalidateQueries({ queryKey: ["/api/hubs"] });
    await queryClient.invalidateQueries({ queryKey: ["/api/current-profile", dbUser.id] });
    await queryClient.invalidateQueries({ queryKey: ["/api/hubs/managed"] });
    if (onHubCreated && createdHub?.id) onHubCreated(createdHub.id);
  };

  if (!open) return null;
  if (isLoadingUser) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading your profile...</p>
      </div>
    </div>
  );
  if (userError) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        <p className="text-red-600 font-medium mb-4">Could not load your account. Please try again.</p>
        <button onClick={onClose} className="px-4 py-2 bg-primary text-white rounded-md">Go Back</button>
      </div>
    </div>
  );
  if (!dbUser?.id) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        <p className="text-gray-700 font-medium mb-4">Please log in to create a hub.</p>
        <button onClick={onClose} className="px-4 py-2 bg-primary text-white rounded-md">Go Back</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 animate-in zoom-in-95 overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-semibold mb-4">
          {editMode ? "Edit Care Recipient Information" : resourceType ? `Create a Hub to Save Your ${resourceType}` : "Setup Care Recipient Hub"}
        </h2>
        {!editMode && liteHasHub && (
          <div className="p-3 mb-4 rounded-md bg-amber-100 border border-amber-300 text-amber-900">
            Lite users can only have one hub. Upgrade to Plus to create multiple hubs.
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-base font-medium text-foreground border-b pb-2">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="firstName">{editMode ? "Hub Name *" : "Care Recipient Name *"}</Label>
              <Input
                id="firstName"
                data-testid="hub-name-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={editMode ? "Enter Hub name" : "Enter first name"}
                required
              />
            </div>

            {editMode && isHubOwner && profileId && !recipientInactive && (
              <div className="space-y-2">
                <Label htmlFor="careRecipientName">Care Recipient Name *</Label>
                <Input
                  id="careRecipientName"
                  value={careRecipientName}
                  onChange={(e) => setCareRecipientName(e.target.value)}
                  placeholder="Enter Care Recipient name"
                  required
                />
              </div>
            )}

            {(!editMode || (isHubOwner && profileId && !recipientInactive)) && (
              <div className="space-y-2">
                <Label>{editMode ? "Care Recipient Photo" : "Profile Image"}</Label>
                <div className="flex items-center gap-4">
                  {avatarUrl ? (
                    <div className="relative">
                      <img src={avatarUrl} alt="Care Recipient" className="w-16 h-16 rounded-full object-cover border-2 border-border" />
                      <button
                        type="button"
                        onClick={() => setAvatarUrl(null)}
                        className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                      <Camera className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-muted transition-colors">
                      <Camera className="h-4 w-4" />
                      {avatarUrl ? "Change Photo" : "Upload Photo"}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Communication Style</Label>
              <MultiSelect
                options={COMMUNICATION_OPTIONS}
                selected={prefs.communicationStyle}
                onChange={(val) => updatePrefs({ communicationStyle: val })}
              />
            </div>

            <div className="space-y-2">
              <Label>Learning & Support Profile</Label>
              <MultiSelect
                options={LEARNING_PROFILE_OPTIONS}
                selected={prefs.learningProfile}
                onChange={(val) => updatePrefs({ learningProfile: val })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="strengths">Strengths</Label>
              <Textarea
                id="strengths"
                value={prefs.strengths}
                onChange={(e) => updatePrefs({ strengths: e.target.value })}
                placeholder="Their strengths and abilities"
                rows={2}
              />
              <SuggestedTags
                suggestions={STRENGTH_SUGGESTIONS}
                value={prefs.strengths}
                onAdd={(tag) => updatePrefs({ strengths: prefs.strengths ? prefs.strengths + ", " + tag : tag })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatWorks">What Works for Me</Label>
              <Textarea
                id="whatWorks"
                value={prefs.whatWorks}
                onChange={(e) => updatePrefs({ whatWorks: e.target.value })}
                placeholder="Strategies and approaches that work well"
                rows={2}
              />
              <SuggestedTags
                suggestions={WHAT_WORKS_SUGGESTIONS}
                value={prefs.whatWorks}
                onAdd={(tag) => updatePrefs({ whatWorks: prefs.whatWorks ? prefs.whatWorks + ", " + tag : tag })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionalNotes">Additional Notes</Label>
              <Textarea
                id="additionalNotes"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Any other important information caregivers should know"
                rows={3}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base font-medium text-foreground border-b pb-2">Preferences</h3>

            <div className="space-y-2">
              <Label>Interests & Motivators</Label>
              <TagInput
                tags={prefs.interests}
                onChange={(val) => updatePrefs({ interests: val })}
                suggestions={INTEREST_SUGGESTIONS}
                placeholder="Add interests (type and press Enter)"
              />
            </div>

            <div className="space-y-2">
              <Label>Dislikes / Triggers</Label>
              <TagInput
                tags={prefs.triggers}
                onChange={(val) => updatePrefs({ triggers: val })}
                suggestions={TRIGGER_SUGGESTIONS}
                placeholder="Add triggers (type and press Enter)"
              />
            </div>

            <div className="space-y-2">
              <Label>Sensory Needs</Label>
              <MultiSelect
                options={SENSORY_OPTIONS}
                selected={prefs.sensoryNeeds}
                onChange={(val) => updatePrefs({ sensoryNeeds: val })}
              />
            </div>

            <div className="space-y-2">
              <Label>Social Comfort</Label>
              <Select
                value={prefs.socialComfort}
                onValueChange={(val) => updatePrefs({ socialComfort: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select social comfort level" />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_COMFORT_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Regulation Supports</Label>
              <MultiSelect
                options={REGULATION_OPTIONS}
                selected={prefs.regulationSupports}
                onChange={(val) => updatePrefs({ regulationSupports: val })}
              />
            </div>
          </div>

          {editMode && showCareRecipientSettings && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="care-recipient">
                <AccordionTrigger>Care Recipient View Settings</AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enableCareRecipientView">Enable Care Recipient View</Label>
                    <Switch
                      id="enableCareRecipientView"
                      checked={enableCareRecipientView}
                      onCheckedChange={setEnableCareRecipientView}
                    />
                  </div>
                  {enableCareRecipientView && (
                    <div className="space-y-2">
                      <Label htmlFor="careRecipientPin">4-Digit PIN *</Label>
                      <Input
                        id="careRecipientPin"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{4}"
                        maxLength={4}
                        value={careRecipientPin}
                        onChange={(e) => setCareRecipientPin(e.target.value.replace(/\D/g, ''))}
                        placeholder="Enter 4-digit PIN"
                      />
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          <div className="flex justify-end space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { resetForm(); onClose(); }}>Cancel</Button>
            <Button type="submit" data-testid="hub-submit-button" disabled={!firstName.trim() || (enableCareRecipientView && careRecipientPin.length !== 4) || liteHasHub}>
              {editMode ? "Update Hub" : "Create Hub"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
